import { Box, Chip, ChipProps, Stack, Tooltip, Typography } from '@mui/material';
import React, { useCallback, useMemo, useState } from 'react';
import { Reference } from 'sr-types/lib/shortlist/v1/graphql';
import I18n from '../i18n/I18n';

const OVERFLOW_THRESHOLD = 4;

export interface MultipleChipPreviewProps {
    data: Reference[];
    chipIconRenderer?: (chipData: Reference) => ChipProps['icon'];
    chipLabelProps?: ChipProps['slotProps']['label'];
}

export default function MultipleChipPreview({ data, chipIconRenderer, chipLabelProps }: MultipleChipPreviewProps) {
    const [isTooltipOpen, setIsTooltipOpen] = useState(false);

    const handleTooltipOpen = useCallback(() => {
        setIsTooltipOpen(true);
    }, []);

    const handleTooltipClose = useCallback(() => {
        setIsTooltipOpen(false);
    }, []);

    const [displayData, overflowData, hasOverflowData] = useMemo(() => {
        let displayData = data;
        let overflowData: Reference[] | undefined = undefined;

        if (data.length > OVERFLOW_THRESHOLD) {
            displayData = data.slice(0, OVERFLOW_THRESHOLD);
            overflowData = data.slice(OVERFLOW_THRESHOLD);
        }

        return [displayData, overflowData, overflowData !== undefined];
    }, [data]);

    return (
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            {displayData.map((dataItem, index) => (
                <Chip
                    icon={chipIconRenderer?.(dataItem)}
                    label={dataItem?.label}
                    variant="outlined"
                    key={`${dataItem?.label}-${index}`}
                    slotProps={{ label: chipLabelProps }}
                />
            ))}
            {hasOverflowData && (
                <Tooltip
                    title={
                        <Stack spacing={0.5}>
                            {overflowData?.map((dataItem, index) => (
                                <Typography key={index}>{dataItem.label}</Typography>
                            ))}
                        </Stack>
                    }
                    open={isTooltipOpen}
                    onClose={handleTooltipClose}
                    disableFocusListener
                    disableHoverListener
                    disableTouchListener
                >
                    <Chip
                        label={<I18n token="multiple.chip.more.label" values={{ value: overflowData?.length ?? 0 }} />}
                        variant="outlined"
                        onMouseEnter={handleTooltipOpen}
                        onMouseLeave={handleTooltipClose}
                    />
                </Tooltip>
            )}
        </Box>
    );
}
